/* eslint-disable */
import { useContext, useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";

import { useAppDispatch, useAppSelector } from "../../store/store";
import { checkActivationCode, confirmNewPassword } from "../../store/authSlice";
import { ActivateAccountRequest } from "../../models/request/accountRequest";
import { LocalizationContext } from "../../App";
import { readDataProtectionRegulations } from "../../store/appSlice";
import { CheckCircle, ClosedEye, EmptyCircle, OpenedEye } from "../../common/icons";
import { AuthLayout } from "../../layout/AuthLayout";

function ConfirmNewPassword() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { code } = useParams();
  const l10n = useContext(LocalizationContext);
  const schema = yup
    .object({
      newPassword: yup
        .string()
        .required(l10n.error.newPasswordIsRequired)
        .min(8, l10n.error.atLeast8CharsLong)
        .matches(/(?=.*[A-Z])/, l10n.error.atLeast1UppercaseLetter)
        .matches(/(?=.*[!@#$%^&*()\-_+.])/, l10n.error.atLeast1SpecialChar)
        .matches(/(?=.*\d)/, l10n.error.atLeast1Numeral)
        .matches(/(?=.*[a-z])/, l10n.error.atLeast1LowercaseLetter),
      confirmPassword: yup
        .string()
        .required(l10n.error.confirmPasswordIsRequired)
        .oneOf([yup.ref("newPassword")], l10n.error.passwordsMustMatch),
    })
    .required();
  const {
    register,
    handleSubmit,
    formState: { isDirty, isValid, errors },
  } = useForm({
    criteriaMode: "all",
    defaultValues: { newPassword: "", confirmPassword: "" },
    mode: "onChange",
    resolver: yupResolver(schema),
  });
  const hasReadPrivacyPolicy = useAppSelector((state) => state.app.hasReadPrivacyPolicy);
  const isActivationCodeValid = useAppSelector((state) => state.auth.activationCodeValid);

  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  const togglePasswordVisibility = (isNewPassword: boolean): void => {
    if(isNewPassword)
      setIsNewPasswordVisible((prevState) => !prevState);
    else
      setIsConfirmPasswordVisible((prevState) => !prevState);
  }

  const validationCriteria: [string, string][] = [
    ["min", l10n.error.atLeast8CharsLong],
    ["matches", l10n.error.atLeast1UppercaseLetter],
    ["matches", l10n.error.atLeast1LowercaseLetter],
    ["matches", l10n.error.atLeast1Numeral],
    ["matches", l10n.error.atLeast1SpecialChar],
    ["oneOf", l10n.error.passwordsMustMatch]
  ];

  useEffect(() => {
    if (pathname.includes("confirmNewPassword")) {
      dispatch(readDataProtectionRegulations(false));
    }

    if (code == undefined) {
      navigate("/login");
    } else if (code && pathname.includes("confirmNewPassword")) {
      dispatch(checkActivationCode(code));
    }
  }, []);

  useEffect(() => {
    if (isActivationCodeValid !== undefined && !isActivationCodeValid) {
      navigate("/login");
    }
  }, [isActivationCodeValid]);

  const isSatisfied = (type: string, criterion: string): boolean => {
    if (!isDirty) return false;

    let isSatisfied = true;
    if (errors.newPassword?.types || errors.confirmPassword?.types) {
      Object.entries(
        {
          ...errors.newPassword?.types,
          ...errors.confirmPassword?.types
        })
        .forEach(([t, r]) => {
          if (t === type && typeof r === "string" && r === criterion) {
            isSatisfied = false;
          }

          if (t === type && Array.isArray(r) && r.findIndex((i) => i === criterion) > -1) {
            isSatisfied = false;
          }
        });
    }

    return isSatisfied;
  };

  const onSubmit = (data: any) => {
    const accountRequest = new ActivateAccountRequest();
    accountRequest.code = code;
    accountRequest.password = data.newPassword;
    dispatch(confirmNewPassword({ activateAccountRequest: accountRequest }));

    navigate("/login");
  };

  if (isActivationCodeValid == undefined && pathname.includes("confirmNewPassword"))
    return null;

  return (
    <AuthLayout>
      {(!hasReadPrivacyPolicy && pathname.includes("confirmNewPassword")) ? (
        <div className="flex-1 flex flex-col justify-between landscape:pb-4 overflow-x-visible overflow-y-scroll">
          <h2>{l10n.title.pleaseReadAndConfirmPrivacyPolicy}</h2>
          <div className="card space-y-4 flex-shrink max-h-min min-h-[100px] overflow-y-scroll">
            <div className="card-body">
              <p dangerouslySetInnerHTML={{ __html: l10n.dataProtec.paragraph1 }}></p>
              <div dangerouslySetInnerHTML={{ __html: l10n.dataProtec.paragraph2 }}></div>
            </div>
          </div>
          <div>
            <button
              className="btn btn-primary"
              onClick={() => dispatch(readDataProtectionRegulations(true))}
            >
              {l10n.btn.iAgree}
            </button>
            <button className="btn btn-secondary">{l10n.btn.iDoNotAgree}</button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 flex flex-col gap-4 justify-between">
            <form
              className="flex-1 flex flex-col gap-5 justify-between landscape:pb-8"
              onSubmit={handleSubmit(onSubmit)}
            >
              <h2>{l10n.title.setPasswordForYourPersonalArea}</h2>

              {/* Added hidden input per: https://www.chromium.org/developers/design-documents/create-amazing-password-forms/#use-hidden-fields-for-implicit-information */}
              <input
                autoComplete="username"
                className="absolute" // So it does not disrupt flow of elements
                hidden
                id="username"
                name="username"
                readOnly
                type="text"
                value="" // TODO: Get username from URL and populate
              />

              <div className="flex flex-col">
                <div className="form-group">
                  <label hidden htmlFor="newPassword">
                    {l10n.label.newPassword}
                  </label>
                  <div className="input-group">
                    <input
                      autoComplete="new-password"
                      className="form-control"
                      id="newPassword"
                      placeholder={l10n.label.newPassword}
                      type={isNewPasswordVisible ? "text" : "password"}
                      {...register("newPassword", { deps: ["confirmPassword"] })}
                    />
                    <div
                      className="absolute inset-y-0 end-0 flex items-center z-20 pe-input-x"
                      onClick={() => togglePasswordVisibility(true)}
                    >
                      {isNewPasswordVisible ? (
                        <ClosedEye className="w-5 h-5 stroke-input-group-icon" />
                      ) : (
                        <OpenedEye className="w-5 h-5 stroke-input-group-icon" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label hidden htmlFor="confirmPassword">
                    {l10n.label.confirmPassword}
                  </label>
                  <div className="input-group">
                    <input
                      autoComplete="new-password"
                      className="form-control"
                      id="confirmPassword"
                      placeholder={l10n.label.confirmPassword}
                      type={isConfirmPasswordVisible ? "text" : "password"}
                      {...register("confirmPassword", { deps: ["newPassword"] })}
                    />
                    <div
                      className="absolute inset-y-0 end-0 flex items-center z-20 pe-input-x"
                      onClick={() => togglePasswordVisibility(false)}
                    >
                      {isConfirmPasswordVisible ? (
                        <ClosedEye className="w-5 h-5 stroke-input-group-icon" />
                      ) : (
                        <OpenedEye className="w-5 h-5 stroke-input-group-icon" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="card relative">
                  <div className="card !p-0 absolute -top-[0.425rem] left-24 w-3 h-3 rotate-45 border-b-0 border-r-0 rounded-none shadow-none"></div>
                  <div className="card-body">
                    <ul className="flex flex-col list-image-none list-inside leading-none">
                      {validationCriteria.map((c) => (
                        <li className="inline-block mb-3 last:mb-0" key={`${c[0]}-${c[1]}`}>
                          <span className="flex leading-none">
                            {isSatisfied(c[0], c[1]) ? (
                              <CheckCircle className="shrink-0 inline-block mr-2 w-5 h-5 stroke-2 stroke-success" />
                            ) : (
                              <EmptyCircle className="shrink-0 inline-block mr-2 w-5 h-5 stroke-2 stroke-muted" />
                            )}
                            <span className="mt-[2px]">{c[1]}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <button className="btn btn-primary" disabled={!isValid} type="submit">
                {l10n.btn.confirmPassword}
              </button>
            </form>
          </div>
        </>
      )}
    </AuthLayout>
  );
}

export default ConfirmNewPassword;
